// src/components/VoucherList.js
import React, { useState, useEffect } from 'react';
import {
    Typography, Box, IconButton, CircularProgress, useMediaQuery, useTheme,
    Snackbar, Alert, TextField, MenuItem, Tooltip, Button, Grid,
} from '@mui/material';
import { FiArrowLeft, FiTag } from 'react-icons/fi';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// Normalize backend status to frontend-friendly status
const normalizeStatus = (status) => {
    switch (status) {
        case 'new': return 'new';
        case 'running': return 'active';
        case 'expired': return 'inactive';
        default: return status;
    }
};

const getStatusString = (status) => {
    switch (status) {
        case 'new': return 'New';
        case 'active': return 'Running';
        case 'inactive': return 'Expired';
        default: return status;
    }
};

const VoucherList = ({ filterStatus, icon }) => {
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');

    const navigate = useNavigate();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // Filters
    const [selectedExam, setSelectedExam] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedTopic, setSelectedTopic] = useState('');

    const [exams, setExams] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [topics, setTopics] = useState([]);

    // Fetch vouchers
    useEffect(() => {
        const fetchVouchers = async () => {
            setLoading(true);
            try {
                const res = await api.get('/vouchers');
                const vouchersArray = Array.isArray(res.data.data) ? res.data.data : [];
                setVouchers(
                    vouchersArray.map(v => ({
                        ...v,
                        status: normalizeStatus(v.status),
                        student_name: v.student_name || '-',
                        student_mobile: v.student_mobile || '-',
                        exam_name: v.exam_name || '-',
                        subject_name: v.subject_name || '-',
                        topic_name: v.topic_name || '-',
                    }))
                );
            } catch (err) {
                console.error("Failed to fetch vouchers", err);
                setError("Failed to load vouchers");
                setOpen(true);
            } finally {
                setLoading(false);
            }
        };
        fetchVouchers();
    }, []);

    // Fetch exams
    useEffect(() => {
        api.get('/exams')
            .then(res => setExams(res.data))
            .catch(err => console.error('Error fetching exams:', err));
    }, []);

    // Fetch subjects
    useEffect(() => {
        if (selectedExam) {
            api.get(`/subjects?exam_id=${selectedExam}`)
                .then(res => setSubjects(res.data))
                .catch(err => console.error('Error fetching subjects:', err));
        } else {
            setSubjects([]);
            setSelectedSubject('');
            setTopics([]);
            setSelectedTopic('');
        }
    }, [selectedExam]);

    // Fetch topics
    useEffect(() => {
        if (selectedSubject) {
            api.get(`/topics?subject_id=${selectedSubject}`)
                .then(res => setTopics(res.data))
                .catch(err => console.error('Error fetching topics:', err));
        } else {
            setTopics([]);
            setSelectedTopic('');
        }
    }, [selectedSubject]);

    // Filter vouchers
    const filteredVouchers = vouchers.filter(voucher => {
        const matchesStatus = filterStatus ? voucher.status === filterStatus : true;
        const matchesExam = selectedExam ? voucher.exam_id == selectedExam : true;
        const matchesSubject = selectedSubject ? voucher.subject_id == selectedSubject : true;
        const matchesTopic = selectedTopic ? voucher.topic_id == selectedTopic : true;
        return matchesStatus && matchesExam && matchesSubject && matchesTopic;
    });

    // Columns for DataGrid
    const columns = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'code', headerName: 'Voucher Code', flex: 1 },
        { field: 'student_name', headerName: 'Student Name', flex: 1 },
        { field: 'student_mobile', headerName: 'Mobile Number', flex: 1 },
        { field: 'exam_name', headerName: 'Exam', flex: 1 },
        { field: 'subject_name', headerName: 'Subject', flex: 1 },
        { field: 'topic_name', headerName: 'Topic', flex: 1 },
        { field: 'expiry_date', headerName: 'Expiry Date', flex: 0.8 },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => {
                const statusStr = getStatusString(params.value);
                let color = statusStr === 'Running' ? 'green' : statusStr === 'Expired' ? 'red' : 'blue';
                return <span style={{ color, fontWeight: 600 }}>{statusStr}</span>;
            }
        }
    ];

    // Export to Excel
    const handleExport = () => {
        const exportData = filteredVouchers.map(v => ({
            ID: v.id,
            Code: v.code,
            Student: v.student_name,
            Mobile: v.student_mobile,
            Exam: v.exam_name,
            Subject: v.subject_name,
            Topic: v.topic_name,
            Expiry: v.expiry_date,
            Status: getStatusString(v.status),
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Vouchers');
        const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'vouchers.xlsx');
    };

    return (
        <Box p={2} sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box mb={3} display="flex" flexDirection={isSmallScreen ? 'column' : 'row'} justifyContent="space-between" alignItems="center" gap={2}>
                <Box display="flex" alignItems="center" justifyContent={isSmallScreen ? 'center' : 'flex-start'}>
                    <IconButton onClick={() => navigate(-1)} sx={{
                        borderRadius: '12px',
                        p: '8px 12px',
                        background: 'linear-gradient(135deg, #00bcd4, #007bff)',
                        color: '#fff',
                        '&:hover': { background: 'linear-gradient(135deg, #007bff, #00bcd4)', transform: 'scale(1.05)' }
                    }}>
                        <FiArrowLeft />
                        {!isSmallScreen && <Typography variant="button" sx={{ ml: 1, fontWeight: 600 }}>Back</Typography>}
                    </IconButton>
                </Box>

                <Box display="flex" alignItems="center" justifyContent="center" gap={1} flex={1}>
                    <Box sx={{ fontSize: '2rem', background: '#e3f2fd', borderRadius: '50%', p: 1, color: '#1976d2' }}>
                        {icon || <FiTag />}
                    </Box>
                    <Typography variant={isSmallScreen ? 'h6' : 'h5'} sx={{
                        fontWeight: 'bold',
                        background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textTransform: 'uppercase',
                        letterSpacing: '1.2px',
                        px: 1
                    }}>
                        {filterStatus === 'new' ? 'New Vouchers' : filterStatus === 'active' ? 'Running Vouchers' : 'Expired Vouchers'}
                    </Typography>
                </Box>

                <Box>
                    <Tooltip title="Download data as CSV">
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExport}
                            disabled={filteredVouchers.length === 0}
                        >
                            Export CSV
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            {/* Filters */}
            <Grid container spacing={2} mb={4}>
                {/* Exam */}
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        select
                        label="Select Exam"
                        value={selectedExam}
                        onChange={e => setSelectedExam(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {exams.map(exam => <MenuItem key={exam.id} value={exam.id}>{exam.title}</MenuItem>)}
                    </TextField>
                </Grid>

                {/* Subject */}
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        select
                        label="Select Subject"
                        value={selectedSubject}
                        onChange={e => setSelectedSubject(e.target.value)}
                        disabled={!selectedExam}
                    >
                        <MenuItem value="">All</MenuItem>
                        {subjects.map(sub => <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>)}
                    </TextField>
                </Grid>

                {/* Topic */}
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        select
                        label="Select Topic"
                        value={selectedTopic}
                        onChange={e => setSelectedTopic(e.target.value)}
                        disabled={!selectedSubject}
                    >
                        <MenuItem value="">All</MenuItem>
                        {topics.map(topic => <MenuItem key={topic.id} value={topic.id}>{topic.title}</MenuItem>)}
                    </TextField>
                </Grid>
            </Grid>

            {/* DataGrid */}
            {loading ? (
                <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
            ) : (
                <DataGrid
                    rows={filteredVouchers.map((v, i) => ({ ...v, id: v.id || i }))}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectionOnClick
                    autoHeight
                    sx={{
                        '& .MuiDataGrid-columnHeaders': { background: '#e0f3ff', color: '#004c99', fontWeight: 'bold' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#fef7e0' },
                    }}
                />
            )}

            {/* Snackbar */}
            <Snackbar open={open} autoHideDuration={4000} onClose={() => setOpen(false)}>
                <Alert severity="error" onClose={() => setOpen(false)}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default VoucherList;
